import { useState, useEffect, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { Form, Row, Col, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FaCloudUploadAlt } from "react-icons/fa";
import Select from "react-select";
import { BASE_URL, HEADERS } from "../../../utils/constant";

const MAX_IMAGES = 3;

const ServiceModal = ({
  show,
  handleClose,
  currentService,
  fetchServicesData,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    serviceName: "",
    servicePrice: "",
    // serviceContactNumber: "",
    serviceDescription: "",
    serviceImages: [],
    serviceSubCategory: null,
  });

  const [errors, setErrors] = useState({});
  const inputRefs = useRef([]);
  const [suballServices, setSubAllServices] = useState([]);
  useEffect(() => {
    fetchServiceSubData();
  }, []);

  const fetchServiceSubData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${BASE_URL}/api/service-sub-categories/?page_size=all`,
        {
          headers: HEADERS,
        },
      );
      setSubAllServices(response.data.results || []);
    } catch (error) {
      // toast.error("Error fetching service categories.");
    } finally {
      setLoading(false);
    }
  };
  const [selectedServiceSubCategory, setSelectedServiceSubCategory] =
    useState(null);

  const serviceOptions = suballServices.map((svc) => ({
    value: svc.id,
    label: svc.service_sub_category_name,
  }));

  useEffect(() => {
    if (currentService) {
      setFormData({
        serviceName: currentService.service_name || "",
        servicePrice: currentService.service_price || "",
        // serviceContactNumber: currentService.service_contact_number || "",
        serviceDescription: currentService.service_description || "",
        serviceImages: [
          { id: 1, file: currentService.service_image1 || null },
          { id: 2, file: currentService.service_image2 || null },
          { id: 3, file: currentService.service_image3 || null },
        ].filter((img) => img.file !== null),
        serviceSubCategory: currentService.service_sub_category.id || null,
      });
      setSelectedServiceSubCategory(
        currentService.service_sub_category.id || null,
      );
    } else {
      resetForm();
    }
  }, [currentService]);

  const resetForm = () => {
    setFormData({
      serviceName: "",
      servicePrice: "",
      // serviceContactNumber: "",
      serviceDescription: "",
      serviceImages: [],
      serviceSubCategory: null,
    });
    setSelectedServiceSubCategory(null);
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name.startsWith("serviceImage")) {
      const id = parseInt(name.split("_")[1], 10);
      const updatedImages = formData.serviceImages.map((img) =>
        img.id === id ? { ...img, file: files[0] } : img,
      );
      setFormData({ ...formData, serviceImages: updatedImages });
    } else {
      setFormData({ ...formData, [name]: value });
    }

    // if (name === "serviceContactNumber" || name === "servicePrice") {
    //   let numericValue = value.replace(/\D/g, ""); // Remove non-numeric characters

    //   if (name === "serviceContactNumber" && numericValue.startsWith("0")) {
    //     numericValue = numericValue.slice(1); // Remove the leading 0 if present
    //   }

    //   setFormData({ ...formData, [name]: numericValue });
    // }
    // Reset the error message for the current input
    setErrors({ ...errors, [name]: "" });
  };

  const handleImageClick = (id) => {
    inputRefs.current[id - 1].click();
  };

  const addImageField = () => {
    if (formData.serviceImages.length < MAX_IMAGES) {
      const newId = formData.serviceImages.length + 1;
      setFormData({
        ...formData,
        serviceImages: [...formData.serviceImages, { id: newId, file: null }],
      });
    } else {
      toast.warning(`Maximum ${MAX_IMAGES} images allowed.`);
    }
  };

  const removeImageField = (id) => {
    setFormData({
      ...formData,
      serviceImages: formData.serviceImages.filter((img) => img.id !== id),
    });
  };

  useEffect(() => {
    return () => {
      formData.serviceImages.forEach((img) => {
        if (img.file instanceof File) {
          URL.revokeObjectURL(img.file);
        }
      });
    };
  }, [formData.serviceImages]);

  const validateForm = () => {
    const newErrors = {};
    const phoneRegex = /^[0-9]{10}$/;

    if (!formData.serviceName.trim())
      newErrors.serviceName = "Service Name is required.";
    if (isNaN(formData.servicePrice))
      newErrors.servicePrice = "A valid number is required.";

    if (!formData.serviceDescription.trim())
      newErrors.serviceDescription = "Service Description is required.";
    if (!formData.serviceSubCategory)
      newErrors.serviceSubCategory = "Please select a subcategory.";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    const payload = new FormData();
    payload.append("service_name", formData.serviceName);
    payload.append("service_price", formData.servicePrice);

    payload.append("service_description", formData.serviceDescription);
    payload.append("service_sub_category", selectedServiceSubCategory);

    formData.serviceImages.forEach((img, index) => {
      if (img.file instanceof File) {
        payload.append(`service_image${index + 1}`, img.file);
      }
    });

    try {
      let response;
      if (currentService) {
        response = await axios.put(
          `${BASE_URL}/api/services/${currentService.id}/`,
          payload,
          {
            headers: { ...HEADERS, "Content-Type": "multipart/form-data" },
          },
        );
      } else {
        response = await axios.post(`${BASE_URL}/api/services/`, payload, {
          headers: { ...HEADERS, "Content-Type": "multipart/form-data" },
        });
      }

      if (response) {
        toast.success(response.data.message);
        fetchServicesData();
        handleClose();
        resetForm();
      }
    } catch (error) {
      if (error.response && error.response.data && error.response.data.error) {
        const errorMessage = error.response.data.error;
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {currentService ? "Edit Service" : "Add Service"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Select
                  options={serviceOptions}
                  value={serviceOptions.find(
                    (option) => option.value === selectedServiceSubCategory,
                  )}
                  onChange={(selected) => {
                    const selectedValue = selected ? selected.value : null;
                    setSelectedServiceSubCategory(selectedValue);
                    setFormData({
                      ...formData,
                      serviceSubCategory: selectedValue,
                    });
                    setErrors({ ...errors, serviceSubCategory: null });
                  }}
                  placeholder="Select Subcategory*"
                  isClearable
                  required
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      borderColor: errors.serviceSubCategory
                        ? "red"
                        : provided.borderColor,
                      boxShadow: "none",
                      "&:hover": { borderColor: "#007bff" },
                    }),
                    menu: (provided) => ({ ...provided, zIndex: 9999 }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isFocused ? "#f1f1f1" : "#fff",
                      color: "#000",
                    }),
                  }}
                />
                {errors.serviceSubCategory && (
                  <span className="text-danger">
                    {errors.serviceSubCategory}
                  </span>
                )}
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Floating className="mb-3">
                <Form.Control
                  type="text"
                  id="serviceName"
                  name="serviceName"
                  placeholder="Service Name"
                  value={formData.serviceName}
                  onChange={handleChange}
                  required
                />
                <label htmlFor="serviceName">Service Name*</label>
                {errors.serviceName && (
                  <span className="text-danger">{errors.serviceName}</span>
                )}
              </Form.Floating>
            </Col>
            <Col>
              <Form.Floating className="mb-3">
                <Form.Control
                  type="number"
                  id="servicePrice"
                  name="servicePrice"
                  placeholder="Service Price"
                  value={formData.servicePrice}
                  onChange={handleChange}
                  required
                />
                <label htmlFor="servicePrice">Service Price*</label>
                {errors.servicePrice && (
                  <span className="text-danger">{errors.servicePrice}</span>
                )}
              </Form.Floating>
            </Col>
          </Row>
          <Row>
            <Col>
              <Form.Floating className="mb-3">
                <Form.Control
                  as="textarea"
                  rows={3}
                  id="serviceDescription"
                  name="serviceDescription"
                  placeholder="Service Description"
                  value={formData.serviceDescription}
                  onChange={handleChange}
                  required
                />
                <label htmlFor="serviceDescription">Service Description*</label>
                {errors.serviceDescription && (
                  <span className="text-danger">
                    {errors.serviceDescription}
                  </span>
                )}
              </Form.Floating>
            </Col>
          </Row>
          <Row>
            <Col className="d-flex align-items-center mb-3">
              <Button
                variant="outline-primary"
                onClick={addImageField}
                disabled={formData.serviceImages.length >= MAX_IMAGES}
              >
                <FontAwesomeIcon icon={faPlusCircle} /> Add Image
              </Button>
            </Col>
          </Row>
          <Row className="mb-3">
            {formData.serviceImages.map((img, idx) => (
              <Col
                key={img.id}
                md={4}
                xs={12}
                className="mb-3 d-flex flex-column align-items-center"
              >
                <div className="position-relative" style={{ width: "100%" }}>
                  {img.file ? (
                    <>
                      <img
                        src={
                          img.file instanceof File
                            ? URL.createObjectURL(img.file)
                            : img.file
                        }
                        alt="Service Image"
                        style={{
                          width: "100%",
                          height: "auto",
                          cursor: "pointer",
                          objectFit: "cover",
                          borderRadius: "5px",
                        }}
                        onClick={() => handleImageClick(img.id)}
                      />
                    </>
                  ) : (
                    <div
                      style={{
                        border: "1px dashed #007bff",
                        borderRadius: "5px",
                        padding: "10px",
                        width: "100%",
                        height: "150px",
                        cursor: "pointer",
                        textAlign: "center",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      onClick={() => handleImageClick(img.id)}
                    >
                      <FaCloudUploadAlt size={50} color="#007bff" />
                      <p>Upload Image</p>
                    </div>
                  )}
                  <input
                    type="file"
                    name={`serviceImage_${img.id}`}
                    accept="image/*"
                    onChange={handleChange}
                    style={{ display: "none" }}
                    ref={(el) => (inputRefs.current[img.id - 1] = el)}
                  />
                  <Button
                    variant="light"
                    className="position-absolute"
                    style={{
                      top: "1px",
                      right: "1px",
                      borderRadius: "50%",
                      width: "25px",
                      height: "25px",
                      padding: "0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.2)",
                      backgroundColor: "#f8f9fa",
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                    }}
                    onClick={() => removeImageField(img.id)}
                    onMouseOver={(e) =>
                      (e.currentTarget.style.backgroundColor = "#dc3545")
                    }
                    onMouseOut={(e) =>
                      (e.currentTarget.style.backgroundColor = "#f8f9fa")
                    }
                  >
                    <FontAwesomeIcon
                      icon={faTimes}
                      style={{ fontSize: "14px", color: "#343a40" }}
                    />
                  </Button>
                </div>
              </Col>
            ))}
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? <Spinner animation="border" size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ServiceModal;
